import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, CalendarDays, Users, X, Download } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const LEAVE_COLORS = {
  annual: 'bg-emerald-500',
  sick: 'bg-red-500',
  emergency: 'bg-orange-500',
  unpaid: 'bg-gray-500',
  mgmt_unpaid: 'bg-gray-400',
  partial_unpaid: 'bg-amber-500',
  maternity: 'bg-pink-500',
  paternity: 'bg-indigo-500',
  public_holiday: 'bg-sky-500',
};
const colorFor = (t) => LEAVE_COLORS[t] || 'bg-teal-500';
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const PortalLeaveCalendar = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('portal_token');
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { year: d.getFullYear(), month: d.getMonth() + 1 }; });
  const [data, setData] = useState({ visible: true, see_all: false, scope: [], leaves: [] });
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/api/employee-portal/leave-calendar`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { year: cursor.year, month: cursor.month },
        });
        setData(res.data);
      } catch (err) {
        if (err.response?.status === 401) navigate('/employee-portal/login');
      } finally {
        setLoading(false);
      }
    };
    load();
    setSelectedDay(null);
  }, [cursor, token, navigate]);

  // Map each ISO date → list of leaves active that day
  const dayMap = useMemo(() => {
    const map = {};
    const first = new Date(cursor.year, cursor.month - 1, 1);
    const last = new Date(cursor.year, cursor.month, 0);
    (data.leaves || []).forEach((lv) => {
      let d = new Date(Math.max(new Date(lv.start_date + 'T00:00:00'), first));
      const end = new Date(Math.min(new Date(lv.end_date + 'T00:00:00'), last));
      while (d <= end) {
        const k = iso(d);
        (map[k] = map[k] || []).push(lv);
        d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
      }
    });
    return map;
  }, [data, cursor]);

  const monthName = new Date(cursor.year, cursor.month - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  const firstWeekday = new Date(cursor.year, cursor.month - 1, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(cursor.year, cursor.month, 0).getDate();
  const todayIso = iso(new Date());

  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const move = (delta) => {
    let m = cursor.month + delta, y = cursor.year;
    if (m < 1) { m = 12; y -= 1; } else if (m > 12) { m = 1; y += 1; }
    setCursor({ year: y, month: m });
  };

  const selectedLeaves = selectedDay ? (dayMap[selectedDay] || []) : [];

  const [downloading, setDownloading] = useState(false);
  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/api/employee-portal/leave-calendar/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { year: cursor.year, month: cursor.month },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `leave_calendar_${cursor.year}_${String(cursor.month).padStart(2, '0')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // ignore
    } finally {
      setDownloading(false);
    }
  };

  if (!data.visible) {
    return (
      <div className="max-w-lg mx-auto p-6 text-center" data-testid="portal-leave-calendar">
        <CalendarDays className="h-10 w-10 mx-auto text-gray-300 mb-2" />
        <p className="text-sm text-gray-500">This view is available to managers and HR only.</p>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto space-y-4" data-testid="portal-leave-calendar">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-emerald-600" /> Leave Calendar
          </h2>
          <p className="text-[11px] text-gray-400 flex items-center gap-1 mt-0.5">
            <Users className="h-3 w-3" />
            {data.see_all ? 'All departments' : (Array.isArray(data.scope) ? data.scope.join(', ') : 'My departments')}
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={downloadPdf} disabled={downloading} data-testid="cal-download-btn" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50">
          <Download className="h-4 w-4 mr-1.5" /> {downloading ? '…' : 'PDF'}
        </Button>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-3">
          <div className="flex items-center justify-between mb-3">
            <button onClick={() => move(-1)} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid="cal-prev-btn"><ChevronLeft className="h-4 w-4" /></button>
            <span className="text-sm font-semibold text-gray-700" data-testid="cal-month-label">{monthName}</span>
            <button onClick={() => move(1)} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid="cal-next-btn"><ChevronRight className="h-4 w-4" /></button>
          </div>

          <div className="grid grid-cols-7 gap-1 text-center mb-1">
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
              <div key={i} className="text-[10px] font-semibold text-gray-400 py-1">{d}</div>
            ))}
          </div>

          {loading ? (
            <div className="py-10 text-center text-sm text-gray-400">Loading…</div>
          ) : (
            <div className="grid grid-cols-7 gap-1">
              {cells.map((d, i) => {
                if (d === null) return <div key={`e${i}`} />;
                const k = iso(new Date(cursor.year, cursor.month - 1, d));
                const items = dayMap[k] || [];
                const isToday = k === todayIso;
                const overlap = items.length > 1;
                return (
                  <button
                    key={k}
                    onClick={() => items.length && setSelectedDay(k)}
                    data-testid={`cal-day-${d}`}
                    className={`aspect-square rounded-lg border p-1 flex flex-col items-center justify-start text-[11px] transition
                      ${isToday ? 'border-emerald-400 ring-1 ring-emerald-300' : 'border-gray-100'}
                      ${items.length ? 'hover:bg-emerald-50 cursor-pointer' : 'cursor-default'}
                      ${overlap ? 'bg-amber-50' : 'bg-white'}`}
                  >
                    <span className={`font-medium ${isToday ? 'text-emerald-600' : 'text-gray-600'}`}>{d}</span>
                    {items.length > 0 && (
                      <div className="flex flex-wrap gap-0.5 justify-center mt-0.5">
                        {items.slice(0, 3).map((lv, j) => (
                          <span key={j} className={`w-1.5 h-1.5 rounded-full ${colorFor(lv.leave_type)}`} />
                        ))}
                        {items.length > 3 && <span className="text-[8px] text-gray-400 leading-none">+{items.length - 3}</span>}
                      </div>
                    )}
                    {overlap && <span className="text-[8px] font-bold text-amber-600 leading-none mt-auto">{items.length}</span>}
                  </button>
                );
              })}
            </div>
          )}
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            <span className="flex items-center gap-1 text-[10px] text-gray-500"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Annual</span>
            <span className="flex items-center gap-1 text-[10px] text-gray-500"><span className="w-2 h-2 rounded-full bg-red-500" /> Sick</span>
            <span className="flex items-center gap-1 text-[10px] text-gray-500"><span className="w-2 h-2 rounded-full bg-sky-500" /> Holiday</span>
            <span className="flex items-center gap-1 text-[10px] text-amber-600"><span className="w-2.5 h-2.5 rounded bg-amber-100 border border-amber-300" /> Overlap</span>
          </div>
        </CardContent>
      </Card>

      {selectedDay && (
        <Card className="border-0 shadow-sm" data-testid="cal-day-detail">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700">
                {new Date(selectedDay + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' })}
                <span className="text-gray-400 font-normal"> · {selectedLeaves.length} on leave</span>
              </h3>
              <button onClick={() => setSelectedDay(null)} className="p-1 rounded hover:bg-gray-100"><X className="h-4 w-4 text-gray-400" /></button>
            </div>
            <div className="space-y-1.5">
              {selectedLeaves.map((m, i) => (
                <div key={i} className="flex items-center gap-2.5 px-3 py-2 bg-gray-50 rounded-lg" data-testid={`cal-detail-${m.employee_code}-${i}`}>
                  <span className={`w-2 h-2 rounded-full ${colorFor(m.leave_type)} flex-shrink-0`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-800 truncate">{m.name}</p>
                    <p className="text-[9px] text-gray-400">{m.department} · <span className="capitalize">{(m.leave_type || '').replace('_', ' ')}</span></p>
                  </div>
                  <Badge className="text-[8px] px-1.5 py-0 bg-gray-100 text-gray-600">
                    {new Date(m.start_date + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                    {' – '}
                    {new Date(m.end_date + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PortalLeaveCalendar;
